import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {PaymentProvider} from '@gatewee/types/provider';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'default-provider',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
  ],
  templateUrl: './default-provider.component.html',
})
export class DefaultProviderComponent implements OnInit {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  // All available payment providers
  availableProviders: PaymentProvider[] = Object.values(PaymentProvider);

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.setting = this.context.data.setting;
    this.editSettingFn = this.context.data.editSettingFn;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    // Parse setting.value - should be a single PaymentProvider string
    let selectedProvider: PaymentProvider | null = null;

    if (this.setting.value) {
      try {
        const parsed =
          typeof this.setting.value === 'string'
            ? JSON.parse(this.setting.value)
            : this.setting.value;

        if (typeof parsed === 'string' && this.isValidProvider(parsed)) {
          selectedProvider = parsed as PaymentProvider;
        } else if (parsed && typeof parsed === 'string') {
          selectedProvider = parsed as PaymentProvider;
        }
      } catch (e) {
        // If parsing fails, try treating as plain string
        if (typeof this.setting.value === 'string') {
          const strValue = this.setting.value.trim();
          if (this.isValidProvider(strValue)) {
            selectedProvider = strValue as PaymentProvider;
          }
        }
      }
    }

    // Create form control for select
    this.formGroup = this.fb.group({
      provider: [selectedProvider || ''],
    });
  }

  private isValidProvider(value: string): boolean {
    return this.availableProviders.includes(value as PaymentProvider);
  }

  getProviderLabel(provider: PaymentProvider): string {
    return this.translate.instant(`enums.paymentProvider.${provider}`);
  }

  async onSubmit(): Promise<void> {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.submitting = true;

    // Get selected provider from form
    const selectedProvider: PaymentProvider | null =
      this.formGroup.get('provider')?.value || null;

    // Create updated setting with selected provider as string
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: selectedProvider || '',
    };

    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: Error) => {
        console.error('Error updating setting:', error);
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
